import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { MailOpen, MessagesSquare, LayoutGrid, Users, PiggyBank, ListChecks, Star, ClipboardList } from 'lucide-react';
import { AnimatedSection } from './AnimatedSection';
export function FeaturesSection() {
  const integrations = [{
    name: 'Gmail',
    logo: 'https://cdn.magicpatterns.com/uploads/i3m9Dz8tVyFX3XYH5aSycT/gmail-icon.svg'
  }, {
    name: 'Outlook',
    logo: 'https://cdn.magicpatterns.com/uploads/wciZK1t5un9NpvyYG4xtuo/outlook-icon.svg'
  }, {
    name: 'iMessage',
    logo: 'https://cdn.magicpatterns.com/uploads/b7Bk8etBDenG3R7oDnec67/imessage-icon.svg'
  }, {
    name: 'WhatsApp',
    logo: 'https://cdn.magicpatterns.com/uploads/j1eFkudG3snN4BnwPPvfbX/whatsapp-icon.svg'
  }, {
    name: 'Google Drive',
    logo: 'https://cdn.magicpatterns.com/uploads/2yqcSmzQqZi9cdSsmgsLpN/google-drive-icon.svg'
  }, {
    name: 'Notion',
    logo: 'https://cdn.magicpatterns.com/uploads/1kGheTjtnQKFkEbYZYttia/notion-icon.svg'
  }, {
    name: 'Airtable',
    logo: 'https://cdn.magicpatterns.com/uploads/bQ3BrNNoSGHPhJ2LPvGch1/airtable-icon.svg'
  }, {
    name: 'Canva',
    logo: 'https://cdn.magicpatterns.com/uploads/iKQEnA2bTqFX8YLk3u7yRT/canva-logo.png'
  }, {
    name: 'OneDrive',
    logo: 'https://cdn.magicpatterns.com/uploads/uQgGV18a3e7tTLcoaM3Puu/onedrive-icon.svg'
  }, {
    name: 'Tripleseat',
    logo: 'https://cdn.magicpatterns.com/uploads/pBvjpazwySPVaj2JeBgC2A/tripleseat-logo.png'
  }];
  const actions = [{
    label: 'Analyze RFPs & Briefs',
    icon: MailOpen,
    description: 'Automatically parses client briefs and extracts key requirements. No event admin needed.'
  }, {
    label: 'Centralize communication',
    icon: MessagesSquare,
    description: 'One thread per event across iMessage, WhatsApp, and email. No coordinator chasing updates.'
  }, {
    label: 'Create proposals',
    icon: LayoutGrid,
    description: 'Generate branded proposals from templates in minutes. No back-and-forth.'
  }, {
    label: 'Organize vendors',
    icon: Users,
    description: 'Rate, track, and find the right vendor for any city. No event assistant on spreadsheets.'
  }, {
    label: 'Stay on budget',
    icon: PiggyBank,
    description: 'Real-time spend tracking across every line item. No manual reconciliation.'
  }, {
    label: 'Coordinate event',
    icon: ListChecks,
    description: 'Run-of-show timelines that sync automatically. No coordinator updating docs.'
  }, {
    label: 'Manage venue',
    icon: Star,
    description: 'Venue details, contracts, and contacts in one place. No digging through emails.'
  }, {
    label: 'Manage guests',
    icon: ClipboardList,
    description: 'RSVPs, dietary needs, and seating, handled without extra event staff.'
  }];
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const pillVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };
  return <section id="features" className="py-20 bg-white" data-id="element-144">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-145">
        {/* Integrations Carousel */}
        <AnimatedSection data-id="element-146">
          <div className="text-center mb-16" data-id="element-147">
            <p className="text-sm font-semibold text-indigo-600 tracking-wide uppercase mb-8" data-id="element-148">
              Everything a remote event coordinator would do, automated
            </p>
            <div className="relative overflow-hidden" data-id="element-149">
              <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" data-id="element-150" />
              <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" data-id="element-151" />
              <div className="flex animate-marquee hover:[animation-play-state:paused]" data-id="element-152">
                {[...integrations, ...integrations].map((item, index) => <div key={index} className="flex items-center space-x-3 px-8 flex-shrink-0" data-id="element-153">
                    <img src={item.logo} alt={item.name} className="h-8 w-8 object-contain opacity-70" data-id="element-154" />
                    <span className="font-medium text-gray-500 whitespace-nowrap" data-id="element-155">
                      {item.name}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Features Heading */}
        <AnimatedSection delay={0.2} data-id="element-156">
          <div className="text-center mb-12" data-id="element-157">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl" data-id="element-158">
              The Event Coordinator You Don't Have to Hire
            </h2>
            <p className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto" data-id="element-159">
              Turnout handles the repetitive coordination work that event
              assistants and admins spend hours on. No recruiting, no training,
              no managing.
            </p>
          </div>
        </AnimatedSection>

        {/* Action Pills Grid — staggered entrance */}
        <motion.div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} data-id="element-160">
          {actions.map((action, index) => {
          const Icon = action.icon;
          return <motion.div key={index} variants={pillVariants} whileHover={{
            scale: 1.02,
            y: -2
          }} transition={{
            type: 'spring',
            stiffness: 400,
            damping: 25
          }} className="group flex items-center gap-5 px-6 py-5 bg-white border border-indigo-100 rounded-2xl shadow-sm hover:shadow-md hover:border-indigo-200 transition-colors duration-200 cursor-default" data-id="element-161">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-indigo-50 group-hover:bg-indigo-100 flex items-center justify-center transition-colors duration-200" data-id="element-162">
                  <Icon className="h-6 w-6 text-indigo-600" data-id="element-163" />
                </div>
                <div className="min-w-0" data-id="element-164">
                  <span className="text-lg font-semibold text-gray-900 block leading-tight" data-id="element-165">
                    {action.label}
                  </span>
                  <span className="text-sm text-gray-500 leading-snug block mt-0.5" data-id="element-166">
                    {action.description}
                  </span>
                </div>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}